// =============================================
// Nature of Document — add / edit form (acctg_doc_nature, migration 142)
// =============================================
// Opened from the Nature of Document picker in Process Docs (Filing + Bookkeeping):
// "+ Add new nature / document type" at the top of the list, "Add “…”" for what was
// typed, or the pencil on a nature's row. A nature is what a document is FOR ("Meal"):
// its related words, the kind of document it is (party side + tax reports go by that),
// its expense type and — for the set of books the document is in — its account head.
//
// Standard natures are the same for everyone; editing one makes this account's own copy
// that replaces it in this account's list only ("Back to standard" removes the copy).
// The parent saves; this form only collects the values.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import SearchableDropdown, { DropdownOption } from '../../../components/ui/SearchableDropdown';
import { setFormDirty, isFormDirty, registerSaveHandler, confirmDiscardChanges } from '../../../lib/unsavedChanges';
import { useDragOffset } from '../../../lib/useDragOffset';

export interface NatureFormValues {
  name: string;
  matchWords: string;
  docuTypeId: string;
  expenseTypeId: string;
  coaId: string;          // account head for `books` ('' = none)
}

export interface NatureFormProps {
  /** null = a new nature */
  editing: { kind: 'own' | 'standard' | 'edited-standard'; values: NatureFormValues } | null;
  /** start values for a new nature (e.g. the name typed into the picker) */
  initial?: Partial<NatureFormValues>;
  docuTypeOptions: DropdownOption[];
  /** empty = no expense types here (Documents / DMS) — the field is hidden */
  expenseTypeOptions: DropdownOption[];
  /** the set of books the account head is set up for; null = hidden (no books chosen / DMS) */
  books: { label: string; coaOptions: DropdownOption[] } | null;
  /** shown instead of the account head when no set of books is chosen yet */
  noBooksNote?: string;
  /** lower-case names already in the list (this nature's own name left out) */
  takenNames: Set<string>;
  onSave: (v: NatureFormValues) => Promise<boolean>;
  /** own: delete it; standard / edited standard: take it off this account's list */
  onRemove?: () => Promise<boolean>;
  /** edited standard only: drop this account's changes */
  onBackToStandard?: () => Promise<boolean>;
  onClose: () => void;
}

const BLANK: NatureFormValues = { name: '', matchWords: '', docuTypeId: '', expenseTypeId: '', coaId: '' };

const NatureFormModal: React.FC<NatureFormProps> = ({
  editing, initial, docuTypeOptions, expenseTypeOptions, books, noBooksNote, takenNames,
  onSave, onRemove, onBackToStandard, onClose,
}) => {
  const start = useMemo<NatureFormValues>(() => ({ ...BLANK, ...(editing ? editing.values : initial) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  const [v, setV] = useState<NatureFormValues>(start);
  const [errors, setErrors] = useState<{ name?: string; kind?: string }>({});
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const drag = useDragOffset();

  const set = (patch: Partial<NatureFormValues>) => { setV(prev => ({ ...prev, ...patch })); setFormDirty(true); };

  const validate = (): boolean => {
    const e: { name?: string; kind?: string } = {};
    const name = v.name.trim();
    if (!name) e.name = 'Give the nature a name, e.g. Meal';
    else if (takenNames.has(name.toLowerCase())) e.name = `“${name}” is already in the list`;
    if (!v.docuTypeId) e.kind = 'Choose the kind of document';
    setErrors(e);
    return !e.name && !e.kind;
  };

  const save = async (): Promise<boolean> => {
    if (busy || !validate()) return false;
    setBusy(true);
    try {
      const ok = await onSave({ ...v, name: v.name.trim().replace(/\s+/g, ' ') });
      if (ok) setFormDirty(false);
      return ok;
    } finally { setBusy(false); }
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  // The app's "Save & continue" saves this form while it is open.
  useEffect(() => {
    registerSaveHandler(() => saveRef.current());
    return () => registerSaveHandler(null);
  }, []);
  useEffect(() => { setTimeout(() => nameRef.current?.focus(), 0); }, []);

  const dismiss = async () => {
    if (isFormDirty() && !(await confirmDiscardChanges())) return;
    onClose();
  };
  const run = async (fn?: () => Promise<boolean>) => {
    if (!fn || busy) return;
    setBusy(true);
    try { if (await fn()) setFormDirty(false); } finally { setBusy(false); }
  };

  const kind = editing?.kind;
  const title = !editing ? 'Add new nature / document type'
    : kind === 'own' ? `Edit “${start.name}”` : `Edit standard nature “${start.name}”`;

  return (
    <div className="pr-mf-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) dismiss(); }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !(e.target as HTMLElement).closest('.sd-wrapper')) { e.stopPropagation(); dismiss(); }
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'Enter')) { e.preventDefault(); e.stopPropagation(); save().then(ok => ok && onClose()); }
      }}>
      <div className="pr-mf-modal pr-nature-modal" role="dialog" aria-modal="true" aria-label={title} style={drag.style}>
        <div className="pr-mf-modal-head pr-modal-drag" onMouseDown={drag.onHeaderMouseDown} title="Drag to move">
          <span className="pr-mf-modal-title">{title}</span>
          <button className="pr-mf-modal-close" onClick={dismiss} title="Close" type="button"><FiX size={18} /></button>
        </div>
        <form className="pr-mf-modal-body pr-form-outlined pr-nature-form"
          onSubmit={(e) => { e.preventDefault(); save().then(ok => ok && onClose()); }}>
          {kind === 'standard' && (
            <p className="pr-nature-note">This is a standard nature. Your changes apply to your account only — everyone else keeps the standard one.</p>
          )}
          {kind === 'edited-standard' && (
            <p className="pr-nature-note">You changed this standard nature for your account. “Back to standard” undoes your changes.</p>
          )}

          <div className="pr-form-group">
            <label className="pr-form-label" htmlFor="pr-nature-name">Nature <span className="required">*</span></label>
            <input id="pr-nature-name" ref={nameRef} className="pr-form-input" type="text" maxLength={100}
              value={v.name} placeholder="e.g. Meal, Fuel, Client lunch"
              onChange={e => set({ name: e.target.value })} />
            {v.name && (
              <button type="button" className="pr-nature-clear" title="Clear" aria-label="Clear the name"
                onClick={() => { set({ name: '' }); nameRef.current?.focus(); }}><FiX size={14} /></button>
            )}
            {errors.name && <span className="pr-form-error">{errors.name}</span>}
          </div>

          <div className="pr-form-group">
            <label className="pr-form-label" htmlFor="pr-nature-words">Related words</label>
            <textarea id="pr-nature-words" className="pr-form-textarea" rows={2}
              value={v.matchWords} placeholder="food, dining, lunch, dinner, buffet"
              onChange={e => set({ matchWords: e.target.value })} />
            <span className="pr-form-hint">Comma-separated. Typing any of them in the picker finds this nature.</span>
          </div>

          <div className="pr-form-group">
            <label className="pr-form-label">Kind of document <span className="required">*</span></label>
            <SearchableDropdown className="pr-form-select" options={docuTypeOptions} value={v.docuTypeId}
              onChange={val => set({ docuTypeId: val })} placeholder="Expense Receipt, Purchase Invoice…" hasError={!!errors.kind} />
            {errors.kind
              ? <span className="pr-form-error">{errors.kind}</span>
              : <span className="pr-form-hint">Decides vendor or customer, and which tax reports count it.</span>}
          </div>

          {expenseTypeOptions.length > 0 && (
            <div className="pr-form-group">
              <label className="pr-form-label">Expense type</label>
              <SearchableDropdown className="pr-form-select" options={expenseTypeOptions} value={v.expenseTypeId}
                onChange={val => set({ expenseTypeId: val })} placeholder="Optional — e.g. Meals & entertainment" />
            </div>
          )}

          {books && (
            <div className="pr-form-group">
              <label className="pr-form-label">Account head — {books.label}</label>
              <SearchableDropdown className="pr-form-select" options={books.coaOptions} value={v.coaId}
                onChange={val => set({ coaId: val })}
                placeholder={books.coaOptions.length ? 'Optional — e.g. Meals & Entertainment' : 'No GL accounts in these books yet'} />
              <span className="pr-form-hint">Bookkeeping uses it for this nature in these books, unless a vendor has its own.</span>
            </div>
          )}

          {!books && noBooksNote && <p className="pr-form-hint pr-nature-books-note">{noBooksNote}</p>}

          <div className="pr-nature-actions">
            {onBackToStandard && (
              <button type="button" className="pr-reject-btn" disabled={busy} onClick={() => run(onBackToStandard)}>Back to standard</button>
            )}
            {onRemove && (
              <button type="button" className="pr-reject-btn" disabled={busy} onClick={() => run(onRemove)}>
                {kind === 'own' ? 'Delete' : 'Remove from my list'}
              </button>
            )}
            <span className="pr-nature-actions-gap" />
            <button type="button" className="pr-nature-cancel" onClick={dismiss}>Cancel</button>
            <button type="submit" className="pr-submit-btn" disabled={busy} title="Save (Ctrl+S)">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NatureFormModal;
